import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { LucideAngularModule, Eye, Mail, Send, X } from 'lucide-angular';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import {
  Communication,
  ComposeContact,
  ComposeEmailPayload,
  ComposePreview,
  EmailTemplate,
} from './communication.model';
import { CommunicationService } from './communication.service';

/** A typed email, such as `ann@example.com`, well-formed enough to offer "create and send". */
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * TVG-343: "Write an email" — the one compose box, opened either from the
 * Communications page (pick anybody) or from a contact record (that contact,
 * already chosen).
 *
 * Mark's decision of 13 Oct 2026 is that staff must be able to write their own
 * words as well as use a template, so a template only ever fills the two boxes
 * in — it never locks them. "Write my own" is the default and leaves them empty.
 *
 * The email goes straight away. There is no scheduling: Mark took it out of
 * the scope on 14 Oct 2026, and the prototype has none either.
 *
 * Preview asks the server to fill the placeholders rather than substituting
 * here, so what is read on screen is the text that is actually delivered and
 * there is only one implementation of the substitution to keep right.
 */
@Component({
  selector: 'app-compose-email-modal',
  standalone: true,
  imports: [ReactiveFormsModule, ButtonComponent, LucideAngularModule],
  templateUrl: './compose-email-modal.component.html',
})
export class ComposeEmailModalComponent implements OnInit {
  @Input() isOpen = false;

  /**
   * Opened from a contact record: the recipient is fixed and the picker is
   * replaced by the contact's name, so staff cannot send Ann's email to Bob by
   * opening the box from Ann's page.
   */
  @Input() fixedContact: ComposeContact | null = null;

  /** The message that was queued, so the opener can put it straight on its list. */
  @Output() sent = new EventEmitter<Communication>();
  @Output() cancelled = new EventEmitter<void>();

  protected readonly MailIcon = Mail;
  protected readonly SendIcon = Send;
  protected readonly EyeIcon = Eye;
  protected readonly XIcon = X;

  protected templates: EmailTemplate[] = [];
  protected placeholders: string[] = [];

  protected contacts: ComposeContact[] = [];
  protected searchingContacts = false;

  /** TVG-386: the contact picked from the predictive search, not merely matched by id. */
  private pickedContact: ComposeContact | null = null;

  protected preview: ComposePreview | null = null;
  protected previewing = false;

  protected submitting = false;
  protected sendError: string | null = null;

  protected readonly contactSearchControl = new FormControl<string>('', { nonNullable: true });

  /**
   * TVG-386: shown inline once the typed email matches nobody, so staff can
   * create the contact and send in one step instead of being sent to the
   * separate Add Contact form first.
   */
  protected readonly newContactForm = new FormGroup({
    firstName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    lastName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
  });

  /**
   * TVG-357: the words this box last put in the two boxes itself, so a refill
   * can tell its own text from something the staff member has written.
   */
  private untouchedTemplateText: { subject: string; body: string } | null = null;

  protected readonly form = new FormGroup({
    contactId: new FormControl<number | null>(null),
    templateKey: new FormControl<string>('', { nonNullable: true }),
    subject: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    body: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
  });

  constructor(private communicationService: CommunicationService) {}

  ngOnInit(): void {
    this.loadTemplates();

    if (this.fixedContact) {
      this.form.controls.contactId.setValue(this.fixedContact.id);
    } else {
      this.searchContacts('');
      this.contactSearchControl.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term) => {
          // Typing again means whatever was picked no longer holds — the
          // picker reopens rather than silently keeping the old choice.
          this.pickedContact = null;
          this.form.controls.contactId.setValue(null);
          this.preview = null;
          this.searchContacts(term);
        });
    }
  }

  /** The contact the email is for, whether fixed or picked. */
  protected get selectedContact(): ComposeContact | null {
    return this.fixedContact ?? this.pickedContact;
  }

  /**
   * TVG-386: once an existing contact is picked, or while the fixed contact is
   * in play, the search box and its results are replaced by the chosen name.
   */
  protected get hasChosenContact(): boolean {
    return this.selectedContact !== null;
  }

  /**
   * TVG-386: the typed search has settled with no match and reads as an email,
   * so the box offers to create that contact and send to it.
   */
  protected get showCreateContact(): boolean {
    if (this.hasChosenContact || this.searchingContacts || this.contacts.length > 0) {
      return false;
    }

    return EMAIL_LIKE.test(this.contactSearchControl.value.trim());
  }

  /**
   * TVG-315: a contact who has asked not to be emailed is shown as such before
   * anything is typed, rather than being refused after the message is written.
   */
  protected get recipientHasRefusedEmail(): boolean {
    return this.selectedContact?.email_permission === false;
  }

  protected get canSend(): boolean {
    if (this.submitting || this.recipientHasRefusedEmail) {
      return false;
    }

    if (!this.form.controls.subject.valid || !this.form.controls.body.valid) {
      return false;
    }

    if (this.hasChosenContact) {
      return true;
    }

    return this.showCreateContact && this.newContactForm.valid;
  }

  /** Pick a contact the search turned up, replacing the search box with their name. */
  protected selectContact(contact: ComposeContact): void {
    this.pickedContact = contact;
    this.form.controls.contactId.setValue(contact.id);
    this.contactSearchControl.setValue(contact.name, { emitEvent: false });
    this.contacts = [];
    this.preview = null;

    // TVG-357: a template that was filled in for the last recipient carries
    // their name and their venue's links. Choosing somebody else refills it,
    // but only while the words are still the template's own.
    this.fillChosenTemplate(this.chosenTemplate);
  }

  /** Drop the chosen contact and reopen the search, e.g. to pick somebody else. */
  protected clearChosenContact(): void {
    this.pickedContact = null;
    this.form.controls.contactId.setValue(null);
    this.contactSearchControl.setValue('', { emitEvent: false });
    this.preview = null;
    this.searchContacts('');
  }

  protected onTemplateChange(key: string): void {
    this.form.controls.templateKey.setValue(key);
    this.preview = null;

    // "Write my own" clears the boxes rather than leaving the last template's
    // words behind under a label that no longer describes them.
    const template = this.templates.find((candidate) => candidate.key === key) ?? null;

    this.form.controls.subject.setValue(template?.subject ?? '');
    this.form.controls.body.setValue(template?.body ?? '');
    this.untouchedTemplateText = template
      ? { subject: template.subject, body: template.body }
      : null;

    this.fillChosenTemplate(template);
  }

  /** Whether both boxes still hold exactly the words this box last put in them. */
  private templateTextIsUntouched(): boolean {
    return (
      this.untouchedTemplateText !== null &&
      this.form.controls.subject.value === this.untouchedTemplateText.subject &&
      this.form.controls.body.value === this.untouchedTemplateText.body
    );
  }

  private get chosenTemplate(): EmailTemplate | null {
    const key = this.form.controls.templateKey.value;

    return this.templates.find((candidate) => candidate.key === key) ?? null;
  }

  /**
   * TVG-357: put the resolved wording in the boxes for templates that ask for
   * it, so staff read and edit the words the customer will see rather than the
   * placeholders behind them.
   *
   * The server does the substitution, the same call the preview button makes,
   * so there is still only one implementation of it.
   */
  private fillChosenTemplate(template: EmailTemplate | null): void {
    const contactId = this.form.controls.contactId.value;

    if (!template?.fill_on_select || contactId == null || !this.templateTextIsUntouched()) {
      return;
    }

    this.communicationService.preview(contactId, template.subject, template.body).subscribe({
      next: (filled) => {
        // Anything the staff member typed, whether before this call or while
        // it was in flight, is theirs and stays: only words this box put there
        // itself are replaced.
        if (this.form.controls.templateKey.value !== template.key || !this.templateTextIsUntouched()) {
          return;
        }

        this.form.controls.subject.setValue(filled.subject);
        this.form.controls.body.setValue(filled.body);
        this.untouchedTemplateText = { subject: filled.subject, body: filled.body };
      },
      // A fill that fails leaves the template as written. The placeholders are
      // still substituted when it is sent, so the email is right either way.
      error: () => {},
    });
  }

  protected onPreview(): void {
    const contactId = this.form.controls.contactId.value;

    if (contactId == null) {
      return;
    }

    this.previewing = true;

    this.communicationService
      .preview(contactId, this.form.controls.subject.value, this.form.controls.body.value)
      .subscribe({
        next: (preview) => {
          this.preview = preview;
          this.previewing = false;
        },
        error: () => {
          this.previewing = false;
          this.preview = null;
        },
      });
  }

  protected onSend(): void {
    if (!this.canSend) {
      return;
    }

    const payload: ComposeEmailPayload = {
      template_key: this.form.controls.templateKey.value || null,
      subject: this.form.controls.subject.value,
      body: this.form.controls.body.value,
    };

    const contactId = this.form.controls.contactId.value;

    if (contactId != null) {
      payload.contact_id = contactId;
    } else {
      payload.new_contact = {
        email: this.contactSearchControl.value.trim(),
        first_name: this.newContactForm.controls.firstName.value.trim(),
        last_name: this.newContactForm.controls.lastName.value.trim(),
      };
    }

    this.submitting = true;
    this.sendError = null;

    this.communicationService
      .send(payload)
      .subscribe({
        next: (response) => {
          this.submitting = false;
          this.sent.emit(response.data);
        },
        error: (error) => {
          this.submitting = false;
          this.sendError = error?.error?.message || 'The email could not be queued. Nothing has been sent.';
        },
      });
  }

  protected onClose(): void {
    this.cancelled.emit();
  }

  reset(): void {
    this.form.reset({
      contactId: this.fixedContact?.id ?? null,
      templateKey: '',
      subject: '',
      body: '',
    });
    this.newContactForm.reset({ firstName: '', lastName: '' });
    this.pickedContact = null;
    this.contacts = [];
    this.preview = null;
    this.sendError = null;
    this.submitting = false;
    this.untouchedTemplateText = null;
    this.contactSearchControl.setValue('', { emitEvent: false });

    if (!this.fixedContact) {
      this.searchContacts('');
    }
  }

  private loadTemplates(): void {
    this.communicationService.templates().subscribe({
      next: (response) => {
        this.templates = response.data ?? [];
        this.placeholders = response.placeholders ?? [];
      },
      // Templates that fail to load cost the dropdown, not the ability to
      // write an email by hand.
      error: () => {
        this.templates = [];
        this.placeholders = [];
      },
    });
  }

  private searchContacts(term: string): void {
    this.searchingContacts = true;

    this.communicationService.contacts(term?.trim() || null).subscribe({
      next: (response) => {
        this.contacts = response.data ?? [];
        this.searchingContacts = false;
      },
      error: () => {
        this.contacts = [];
        this.searchingContacts = false;
      },
    });
  }
}
